import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Loader2, Mail, Rocket, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import ParticleBurst from "@/components/ParticleBurst";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [processing, setProcessing] = useState(true);
  const [success, setSuccess] = useState(false);
  const [credentials, setCredentials] = useState(null); // { email, temp_password, login_url, license_key, tier }
  const [showBurst, setShowBurst] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");
    const email = localStorage.getItem("purchase_email");
    const tier = localStorage.getItem("purchase_tier");

    if (token && email && tier) {
      capturePayment(token, email, tier);
    } else {
      toast.error("Invalid payment session");
      setTimeout(() => navigate("/purchase"), 2000);
    }
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const capturePayment = async (orderId, email, tier) => {
    try {
      const couponCode = localStorage.getItem("pending_coupon");

      const response = await axios.post(`${API}/purchase/capture-order`, {
        order_id: orderId,
        user_email: email,
        tier: tier,
        coupon_code: couponCode || undefined,
      });

      if (response.data.success) {
        setSuccess(true);
        setShowBurst(true);
        toast.success("Payment successful! Your account is ready.");

        // Persist for the user dashboard if they navigate there later
        localStorage.setItem("license_data", JSON.stringify(response.data.license_data));
        localStorage.setItem("user_email", email);

        // Capture app credentials returned by the backend (Option A)
        if (response.data.app_credentials) {
          setCredentials({
            email: response.data.app_credentials.email,
            temp_password: response.data.app_credentials.temp_password,
            login_url: response.data.app_credentials.login_url,
            license_key: response.data.license_data?.license_key,
            tier: tier,
          });
        }

        // Cleanup
        localStorage.removeItem("purchase_email");
        localStorage.removeItem("purchase_tier");
        localStorage.removeItem("pending_coupon");
      }
    } catch (error) {
      toast.error("Payment processing failed");
      console.error(error);
      setTimeout(() => navigate("/purchase"), 2000);
    } finally {
      setProcessing(false);
    }
  };

  const copy = (value, label) => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {showBurst && (
        <ParticleBurst
          count={120}
          duration={2400}
          onDone={() => setShowBurst(false)}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-4">
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">
            Maidly Business
          </h1>
          {!processing && (
            <button
              data-testid="back-to-home-btn"
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="bg-white border border-gray-200 shadow-md max-w-md w-full">
          <CardContent className="p-8 text-center">
            {processing ? (
              <>
                <Loader2 className="w-16 h-16 mx-auto mb-4 animate-spin" style={{ color: '#0073ea' }} />
                <h2 className="text-2xl font-bold mb-2 text-gray-800">Processing Payment...</h2>
                <p className="text-gray-600">Please wait while we confirm your payment</p>
              </>
            ) : success ? (
              <>
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#22c55e' }}>
                  <CheckCircle className="w-12 h-12 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">
                  Your Account is Ready
                </h2>
                <p className="text-gray-600 mb-6">
                  Payment successful! Use the credentials below to log into your Maidly Business account.
                </p>

                {credentials ? (
                  <>
                    <div className="bg-amber-50 border border-amber-200 p-5 mb-5 text-left">
                      <div className="mb-4">
                        <div className="text-xs font-semibold text-amber-800 mb-1 tracking-wider">EMAIL</div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-gray-900 break-all text-sm">{credentials.email}</span>
                          <button
                            onClick={() => copy(credentials.email, 'Email')}
                            className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                            title="Copy email"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold text-amber-800 mb-1 tracking-wider">TEMPORARY PASSWORD</div>
                        <div className="flex items-center justify-between gap-2 bg-white border border-dashed border-amber-300 p-3">
                          <span className="font-mono text-gray-900 break-all text-sm">{credentials.temp_password}</span>
                          <button
                            onClick={() => copy(credentials.temp_password, 'Password')}
                            className="text-amber-700 hover:text-amber-900 flex-shrink-0"
                            title="Copy password"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-amber-800 mt-2">⚠️ Change this after first login (Settings → Security).</p>
                      </div>
                    </div>

                    {credentials.license_key && (
                      <div className="bg-gray-50 border border-gray-200 p-4 mb-5 text-left">
                        <div className="text-xs font-semibold text-gray-700 mb-1 tracking-wider">LICENSE KEY</div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-gray-900 break-all text-xs">{credentials.license_key}</span>
                          <button
                            onClick={() => copy(credentials.license_key, 'License key')}
                            className="text-gray-700 hover:text-gray-900 flex-shrink-0"
                            title="Copy license key"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                ) : null}

                <div className="bg-blue-50 border border-blue-200 p-3 mb-6 flex items-center gap-3">
                  <Mail className="w-5 h-5 flex-shrink-0" style={{ color: '#0073ea' }} />
                  <p className="text-sm text-blue-800 text-left">
                    Credentials & license key emailed to <strong>{credentials?.email}</strong>.
                  </p>
                </div>

                <button
                  data-testid="launch-app-btn"
                  onClick={() => navigate(`/login?email=${encodeURIComponent(credentials?.email || '')}&from=purchase`)}
                  className="w-full py-4 text-white font-semibold transition-all duration-200 flex items-center justify-center gap-2"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                >
                  <Rocket className="w-5 h-5" />
                  Launch App
                </button>

                <button
                  onClick={() => navigate("/user")}
                  className="mt-3 text-sm text-gray-600 hover:text-gray-900"
                >
                  Manage license →
                </button>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold mb-2 text-red-600">Payment Failed</h2>
                <p className="text-gray-600 mb-6">Something went wrong. Please try again.</p>
                <button
                  onClick={() => navigate("/purchase")}
                  className="w-full py-4 text-white font-semibold transition-all duration-200"
                  style={{ backgroundColor: '#0073ea' }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#005bb5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = '#0073ea'}
                >
                  Back to Purchase
                </button>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
